/*
 * BHAKTA product page behaviour, from html/BHAKTA PDP v2.dc.html.
 *
 * Three small components, each usable on its own:
 *   bhakta-gallery       — thumbnail picks the main shot
 *   bhakta-product-form  — quantity stepper, then Add to Cart through the
 *                          drawer's own AJAX so the two never disagree
 *   bhakta-sticky-bar    — reveals itself once the main Add to Cart is above
 *                          the viewport, and mirrors the quantity
 *
 * The form is a real <form action="/cart/add">, so without JavaScript the page
 * still adds to the cart and reloads.
 */

/*
 * Wrapped, because more than one section can ask for this file: a product page
 * loads it, and so does every split band carrying a product spotlight. Two tags
 * with the same src both run, and a second pass over top-level class
 * declarations is a SyntaxError that kills the whole file — including the parts
 * the first pass had not reached. Inside a function the second pass is
 * harmless, and the customElements guards at the foot do the rest.
 */
(() => {
  class BhaktaGallery extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-bk-gallery-main]');
      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-bk-shot]');
        if (thumb) this.pick(thumb);
      });
    }

    pick(thumb) {
      if (!this.main) return;

      /* The main frame is markup, not a background, so the alt text travels too. */
      this.main.innerHTML = thumb.dataset.bkShot;

      this.querySelectorAll('[data-bk-shot]').forEach((el) => {
        el.classList.toggle('is-active', el === thumb);
        el.setAttribute('aria-current', el === thumb ? 'true' : 'false');
      });
    }
  }

  class BhaktaProductForm extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-bk-qty-input]');
      this.output = this.querySelector('[data-bk-qty-value]');
      this.max = parseInt(this.dataset.max, 10) || 3;
      this.button = this.querySelector('[data-bk-submit]');
      this.addedLabel = this.dataset.addedLabel || 'Added to Cart';
      this.idleLabel = this.button ? this.button.textContent.trim() : '';

      this.addEventListener('click', (event) => this.onClick(event));
    }

    onClick(event) {
      const step = event.target.closest('[data-bk-step]');
      if (step) {
        event.preventDefault();
        this.setQuantity(this.quantity + parseInt(step.dataset.bkStep, 10));
        return;
      }

      /*
       * Without a drawer on the page there is nothing to open and nothing to
       * refresh, so the form is left to submit and reload the page.
       */
      if (event.target.closest('[data-bk-submit]') && window.bhaktaCart) {
        event.preventDefault();
        this.submit();
      }
    }

    get quantity() {
      return parseInt(this.input?.value, 10) || 1;
    }

    setQuantity(value) {
      const next = Math.min(this.max, Math.max(1, value));
      if (this.input) this.input.value = next;
      if (this.output) this.output.textContent = next;

      /* The sticky bar shows the same number, wherever it was changed. */
      document.querySelectorAll('[data-bk-qty-mirror]').forEach((el) => {
        el.textContent = next;
      });
    }

    async submit() {
      const variant = this.dataset.variant;
      if (!variant || this.busy) return;

      this.busy = true;
      this.classList.add('is-busy');

      const ok = await window.bhaktaCart.addVariant(variant, this.quantity);

      this.busy = false;
      this.classList.remove('is-busy');

      if (!ok) return;

      if (this.button) {
        this.button.textContent = this.addedLabel;
        clearTimeout(this.reset);
        this.reset = setTimeout(() => {
          this.button.textContent = this.idleLabel;
        }, 1800);
      }

      window.bhaktaCart.open();
    }
  }

  class BhaktaRelated extends HTMLElement {
    connectedCallback() {
      /*
       * Liquid cannot see recommendations on a cold page render, so the band ships
       * with a fallback collection and swaps itself for the real row once the API
       * answers. Nothing happens if the API has nothing to suggest.
       */
      if (!this.dataset.url) return;

      fetch(this.dataset.url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          if (!html) return;

          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('bhakta-related');
          if (fresh && fresh.querySelector('.bk-related__grid li')) this.innerHTML = fresh.innerHTML;
        })
        .catch(() => {
          /* The fallback row is already on the page, so a failure is not fatal. */
        });
    }
  }

  class BhaktaStickyBar extends HTMLElement {
    connectedCallback() {
      /*
       * The bar watches the real Add to Cart button, not a marker element beside
       * it. An empty div has no layout box, so its rect reads as all zeros and it
       * never appears to scroll above the top — the bar stayed hidden forever.
       * The button always has measurable height, so its position is honest.
       */
      this.trigger = document.querySelector(this.dataset.sentinel);
      if (!this.trigger) return;

      this.measure = this.measure.bind(this);
      this.schedule = this.schedule.bind(this);

      addEventListener('scroll', this.schedule, { passive: true });
      addEventListener('resize', this.schedule, { passive: true });
      this.measure();
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.measure();
      });
    }

    /*
     * Once the button's foot passes the top of the window the offer is off screen
     * and the bar takes over. The window's own top is the line, not the header's:
     * the header floats above the page and would only delay the handover.
     */
    measure() {
      const past = this.trigger.getBoundingClientRect().bottom <= 0;
      this.classList.toggle('is-visible', past);
      this.setAttribute('aria-hidden', past ? 'false' : 'true');
    }

    disconnectedCallback() {
      removeEventListener('scroll', this.schedule);
      removeEventListener('resize', this.schedule);
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  }

  if (!customElements.get('bhakta-gallery')) customElements.define('bhakta-gallery', BhaktaGallery);
  if (!customElements.get('bhakta-product-form')) {
    customElements.define('bhakta-product-form', BhaktaProductForm);
  }
  if (!customElements.get('bhakta-sticky-bar')) {
    customElements.define('bhakta-sticky-bar', BhaktaStickyBar);
  }
  if (!customElements.get('bhakta-related')) customElements.define('bhakta-related', BhaktaRelated);
})();
